import { useState } from 'react';
import { PageHeader } from '../components/Layout';
import { Badge, Button, Card, EmptyState, ErrorNote, Textarea } from '../components/ui';
import { api, type AskCitation, type AskResult } from '../lib/api';
import { dateTime, humanize } from '../lib/format';
import { useAction } from '../lib/hooks';

const EXAMPLES = [
  'Who did I talk to about pricing recently?',
  'What did Acme Corp say in their last reply?',
  'Which contacts have I called in the last month?',
];

export function Ask() {
  const [question, setQuestion] = useState('');
  const [asked, setAsked] = useState<string | null>(null);
  const [result, setResult] = useState<AskResult | null>(null);

  const ask = useAction(async (q: string) => {
    const response = await api.post<AskResult>('/api/ask', { question: q });
    setResult(response);
    setAsked(q);
    return response;
  });

  const submit = () => {
    const trimmed = question.trim();
    if (trimmed.length < 3) return;
    void ask.run(trimmed);
  };

  return (
    <>
      <PageHeader
        title="Ask"
        description="A question about your contact history, answered from the timeline with citations."
        helpHref="ask"
      />

      <Card>
        <div className="flex flex-col gap-3">
          <Textarea
            rows={2}
            placeholder="Who did I talk to about pricing in Q2?"
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
            }}
          />
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              {EXAMPLES.map((example) => (
                <button
                  key={example}
                  type="button"
                  onClick={() => setQuestion(example)}
                  className="rounded-full bg-ink-50 px-3 py-1 text-xs text-ink-600 hover:bg-ink-100"
                >
                  {example}
                </button>
              ))}
            </div>
            <Button
              variant="primary"
              loading={ask.pending}
              disabled={question.trim().length < 3}
              onClick={submit}
            >
              Ask
            </Button>
          </div>
        </div>
      </Card>

      {ask.error && (
        <div className="mt-4">
          <ErrorNote message={ask.error} />
        </div>
      )}

      {result && asked && !ask.pending && (
        <div className="mt-4 flex flex-col gap-4">
          <Card title={`“${asked}”`}>
            <p className="whitespace-pre-wrap text-sm text-ink-800">{result.answer}</p>
            <p className="mt-3 text-xs text-ink-400">
              Considered {result.events_considered} timeline event
              {result.events_considered === 1 ? '' : 's'}.
            </p>
          </Card>

          {result.citations.length > 0 ? (
            <Card title="Citations">
              <ul className="flex flex-col divide-y divide-ink-100">
                {result.citations.map((citation) => (
                  <CitationRow key={citation.event_id} citation={citation} />
                ))}
              </ul>
            </Card>
          ) : (
            <EmptyState
              title="No timeline events support this answer"
              description="Try a different phrasing, or widen the date range implied by the question."
            />
          )}
        </div>
      )}
    </>
  );
}

function CitationRow({ citation }: { citation: AskCitation }) {
  const { event } = citation;
  return (
    <li className="flex flex-col gap-1 py-3 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center gap-2 text-xs text-ink-500">
        {event && <Badge tone="neutral">{humanize(event.kind)}</Badge>}
        {event && <span>{dateTime(event.occurred_at)}</span>}
      </div>
      {event ? (
        <>
          <p className="text-sm font-medium text-ink-900">
            {event.title}
            {(event.prospect_name || event.company_name) && (
              <span className="ml-2 font-normal text-ink-500">
                {[event.prospect_name, event.company_name].filter(Boolean).join(' · ')}
              </span>
            )}
          </p>
          <p className="text-xs text-ink-500">{citation.why}</p>
        </>
      ) : (
        <p className="text-xs text-ink-500">{citation.why}</p>
      )}
    </li>
  );
}
